<script lang="ts" setup>
import {
  Co2QuizQuestion,
  CalculationType,
  CalculationLabel,
  QuestionType,
} from '@/models/co2-quiz.model';
import { PropType } from 'vue';

defineProps({
  question: {
    type: Object as PropType<Co2QuizQuestion>,
    required: true,
  },
  questions: {
    type: Object as PropType<Co2QuizQuestion[]>,
    required: true,
  },
});

const warnings = [
  {
    id: 1,
    type: QuestionType.Answers,
    answers: true,
  },
  {
    id: 2,
    type: QuestionType.UserInput,
    answers: false,
  },
  {
    id: 3,
    type: QuestionType.PartsQuestion,
    parts: true,
    answers: false,
  },
  {
    id: 4,
    calculation: CalculationType.Car,
    labels: [
      CalculationLabel.CarFuel,
      CalculationLabel.CarKM,
      CalculationLabel.CarSharing,
      CalculationLabel.CarPassengers,
    ],
  },
  {
    id: 5,
    calculation: CalculationType.Motor,
    labels: [CalculationLabel.MotorFuel, CalculationLabel.MotorPassengers],
  },
  {
    id: 6,
    calculation: CalculationType.HouseSize,
    labels: [
      CalculationLabel.HouseType,
      CalculationLabel.HouseAge,
      CalculationLabel.HouseOccupants,
    ],
  },
  {
    id: 7,
    calculation: CalculationType.Electricity,
    labels: [
      CalculationLabel.HouseType,
      CalculationLabel.HouseAge,
      CalculationLabel.HouseOccupants,
    ],
    types: [CalculationType.HouseSize],
  },
  {
    id: 8,
    calculation: CalculationType.Diet,
    labels: [
      CalculationLabel.FruitAndVeg,
      CalculationLabel.Organic,
      CalculationLabel.FoodWaste,
    ],
  },
  {
    id: 9,
    calculation: CalculationType.Flying,
    chainQuestion: true,
  },
  {
    id: 10,
    calculation: CalculationType.ElectricityUsage,
    labels: [CalculationLabel.HouseOccupants],
  },
  {
    id: 11,
    calculation: CalculationType.GasUsage,
    labels: [CalculationLabel.HouseOccupants],
  },
  {
    id: 12,
    calculation: CalculationType.Mobility,
    followUp: true,
  },
];

const warningLabels = {
  answers: 'Ensure this question has answers',
  noAnswers: 'This question should not have answers',
  parts: 'This question should have part questions',
  noParts: 'This question should have part questions',
  followUp: 'At least one answer with a follow up should be available',
  chain: 'This question should have a chain question',
  labels: 'A question with the label "{{label}}" should exist',
  types: 'A question with the calculation type "{{type}}" should exist',
};

function applyLabel(v: string, value: string): string {
  return v.replace(/(\{\{.+\}\})/g, value);
}
</script>
<template>
  <v-container style="padding-top: 0; padding-bottom: 0">
    <template v-for="warning in warnings" :key="warning.id">
      <v-alert
        type="warning"
        class="mb-2"
        density="compact"
        v-if="
          warning.type &&
          question.questionType === warning.type &&
          warning.answers &&
          !question.answers.length
        "
        >{{ warningLabels.answers }}</v-alert
      >
      <v-alert
        type="warning"
        class="mb-2"
        density="compact"
        v-if="
          warning.answers === false &&
          question.questionType === warning.type &&
          question.answers.length
        "
        >{{ warningLabels.noAnswers }}</v-alert
      >
      <v-alert
        type="warning"
        class="mb-2"
        density="compact"
        v-if="
          warning.parts &&
          question.questionType === warning.type &&
          !(
            questions.filter((q) => q.partQuestion?.id === question.id)
              .length || question.partQuestions?.length
          )
        "
        >{{ warningLabels.parts }}</v-alert
      >
      <v-alert
        type="warning"
        class="mb-2"
        density="compact"
        v-if="
          warning.followUp &&
          question.calculationType === warning.calculation &&
          !question.answers.find((a) => a.followUpQuestion)
        "
        >{{ warningLabels.followUp }}</v-alert
      >
      <v-alert
        type="warning"
        class="mb-2"
        density="compact"
        v-if="
          warning.chainQuestion &&
          question.calculationType === warning.calculation &&
          !question.chainQuestion
        "
        >{{ warningLabels.chain }}</v-alert
      >
      <template v-for="label in warning.labels" :key="label">
        <v-alert
          type="warning"
          class="mb-2"
          density="compact"
          v-if="
            warning.labels &&
            question.calculationType === warning.calculation &&
            !questions.find((q) => q.calculationLabels.includes(label))
          "
          >{{ applyLabel(warningLabels.labels, label) }}</v-alert
        ></template
      >
      <template v-for="type in warning.types" :key="type">
        <v-alert
          type="warning"
          class="mb-2"
          density="compact"
          v-if="
            warning.types &&
            question.calculationType === warning.calculation &&
            !questions.find((q) => q.calculationType === type)
          "
          >{{ applyLabel(warningLabels.types, type) }}</v-alert
        ></template
      >
    </template>
  </v-container>
</template>
